/**
 * Gift card balance checker
 *
 * Looks up a gift card's live balance using Shopify's public Storefront API.
 * It creates a disposable cart, attempts to apply the entered code as a gift
 * card via the cartGiftCardCodesUpdate mutation, then reads the balance back
 * off the cart's appliedGiftCards field. The disposable cart is never shown
 * to the customer and never touches their real cart.
 *
 * Requires a Storefront API public access token, set in this section's
 * settings (Settings > Apps and sales channels > Headless in the Shopify
 * admin > create a storefront > copy the public access token).
 */
(function () {
  var CART_CREATE_MUTATION = 'mutation cartCreate { cartCreate { cart { id } userErrors { message } } }';

  var GIFT_CARD_UPDATE_MUTATION =
    'mutation cartGiftCardCodesUpdate($cartId: ID!, $giftCardCodes: [String!]!) {' +
    '  cartGiftCardCodesUpdate(cartId: $cartId, giftCardCodes: $giftCardCodes) {' +
    '    cart {' +
    '      appliedGiftCards {' +
    '        balance { amount currencyCode }' +
    '        lastCharacters' +
    '      }' +
    '    }' +
    '    userErrors { message }' +
    '  }' +
    '}';

  function init() {
    var form = document.getElementById('gift-card-balance-form');
    if (!form) return;

    var submitTextEl = form.querySelector('[data-submit-text]');
    if (submitTextEl && !submitTextEl.dataset.defaultText) {
      submitTextEl.dataset.defaultText = submitTextEl.textContent;
    }

    form.addEventListener('submit', function (event) {
      handleSubmit(event, form);
    });
  }

  function handleSubmit(event, form) {
    event.preventDefault();

    var resultEl = document.querySelector('[data-gift-card-balance-result]');
    var input = form.querySelector('[data-code-input]');
    var button = form.querySelector('button[type="submit"]');
    var submitTextEl = form.querySelector('[data-submit-text]');
    var code = (input.value || '').replace(/\s+/g, '');

    if (!code) return;

    var shopDomain = form.dataset.shopDomain;
    var token = form.dataset.storefrontToken;
    var apiVersion = form.dataset.apiVersion || '2026-01';

    if (!token) {
      showResult(resultEl, 'error', form.dataset.msgSetupIncomplete);
      return;
    }

    setLoading(button, submitTextEl, true, form.dataset.msgChecking);
    hideResult(resultEl);

    var endpoint = 'https://' + shopDomain + '/api/' + apiVersion + '/graphql.json';
    var headers = {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': token
    };

    fetchGraphQL(endpoint, headers, CART_CREATE_MUTATION, {})
      .then(function (createResult) {
        var cartCreate = createResult && createResult.data && createResult.data.cartCreate;
        var cart = cartCreate && cartCreate.cart;
        var createErrors = (cartCreate && cartCreate.userErrors) || [];

        if (!cart || createErrors.length) {
          throw new Error('cart_create_failed');
        }

        return fetchGraphQL(endpoint, headers, GIFT_CARD_UPDATE_MUTATION, {
          cartId: cart.id,
          giftCardCodes: [code]
        });
      })
      .then(function (updateResult) {
        var payload = updateResult && updateResult.data && updateResult.data.cartGiftCardCodesUpdate;
        var appliedGiftCards = (payload && payload.cart && payload.cart.appliedGiftCards) || [];
        var userErrors = (payload && payload.userErrors) || [];

        if (appliedGiftCards.length && !userErrors.length) {
          var card = appliedGiftCards[0];
          var formattedBalance = formatMoney(card.balance && card.balance.amount, card.balance && card.balance.currencyCode);
          var template = card.lastCharacters ? form.dataset.msgResultWithChars : form.dataset.msgResult;
          var message = template
            .replace('__AMOUNT__', formattedBalance)
            .replace('__CHARS__', card.lastCharacters || '');

          showResult(resultEl, 'success', message);
        } else {
          showResult(resultEl, 'error', form.dataset.msgNotFound);
        }
      })
      .catch(function () {
        showResult(resultEl, 'error', form.dataset.msgGenericError);
      })
      .then(function () {
        setLoading(button, submitTextEl, false);
      });
  }

  function fetchGraphQL(endpoint, headers, query, variables) {
    return fetch(endpoint, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify({ query: query, variables: variables })
    }).then(function (response) {
      if (!response.ok) throw new Error('network_error');
      return response.json();
    });
  }

  function formatMoney(amount, currencyCode) {
    var value = parseFloat(amount);

    if (isNaN(value) || !currencyCode) {
      return amount + ' ' + (currencyCode || '');
    }

    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: currencyCode
      }).format(value);
    } catch (e) {
      return value.toFixed(2) + ' ' + currencyCode;
    }
  }

  function showResult(el, type, message) {
    if (!el || !message) return;
    el.hidden = false;
    el.textContent = message;
    el.className = 'gift-card-balance__result alert alert--' + (type === 'success' ? 'success' : 'error');
    el.setAttribute('role', 'status');
  }

  function hideResult(el) {
    if (!el) return;
    el.hidden = true;
    el.textContent = '';
  }

  function setLoading(button, submitTextEl, isLoading, loadingText) {
    if (!button) return;
    button.disabled = isLoading;

    if (submitTextEl) {
      submitTextEl.textContent = isLoading ? loadingText : submitTextEl.dataset.defaultText;
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
